import { navigate, replace } from 'components/Navigation/navigationRef';
import { ModalScreens, Screens, Stacks } from 'components/Navigation/types';
import { File } from 'expo-file-system';
import { ScreenSize } from 'lib/constants';
import { isError } from 'lib/isError';
import { pluralise } from 'lib/pluralise';
import { getScreenSize, dateToNumber } from 'lib/riskbase';
import { last } from 'lodash';
import { Platform } from 'react-native';
import {
  GetPhotoErrors,
  getFileTypeFromUntrustedExtension,
  getPhoto,
  readAsDataURLAsync,
  removeDataBase64Prefix,
  resizeImage,
  validateFileSize,
} from 'service/file';
import {
  FILE_TYPES,
  FileSourceType,
  FileTypeGroupsEnum,
  IMAGE_COMPRESSION,
  IMAGE_MAX_HEIGHT,
  IMAGE_MAX_WIDTH,
  MAX_FILE_SIZE_MB,
  THUMBNAIL_COMPRESSION,
  THUMBNAIL_MAX_HEIGHT,
  THUMBNAIL_MAX_WIDTH,
} from 'service/file/types';
import { hasCameraPermission } from 'service/permission';
import {
  getAssetData,
  setAssetData,
  setAssetThumbnail,
  writeLocallyAddedFile,
} from 'service/storage';
import {
  ASSET_SAVE_FIELDS,
  CATCH_ERROR,
  doFilesLoad,
  LOAD_ORGANISATION,
  SHOW_BLOCKING_LOADER,
  UNLOAD_ORGANISATION,
} from 'store/actions';
import {
  ALERT,
  ALERT_ERROR,
  doSetSettingSavePhotosToMediaLibrary,
  doToast,
  eventAssetDataUpdated,
  eventTypeFilesSelected,
  HIDE_BLOCKING_LOADER,
  NAVIGATE_MODAL,
  OPEN_BARCODE_SCANNER,
  OPEN_NATIVE_APP_SETTINGS,
} from 'store/app/actions';
import {
  ASSET_SAVE_REPEATABLE_FIELDS,
  DELETE_CHOSEN_ASSETS,
  doAssetDeleteFieldInstance,
  doAssetFindFieldInstanceByPin,
  doAssetFindFieldInstanceByQR,
  doAssetFoundFieldInstanceByPin,
  doAssetFoundFieldInstanceByQR,
  doAssetLoadPlans,
  doAssetOpenFieldInstance,
  doAssetOpenOverview,
  ENQUEUE_ASSET_DELETE,
  LOAD_ASSET,
  doAssetOpenFiles,
  SET_LOADED_ASSET,
  UNLOAD_ASSET,
  UNSET_LOADED_ASSET,
  doAssetFileGetPhotoFromPhoto,
  doAssetSaveFilesToAsset,
  CHOOSE_ALL_FILTERED_ASSETS,
  CHOOSE_ASSETS,
} from 'store/assets/actions';
import {
  selectAsset,
  selectAssetFieldRepeatableGroupPins,
  selectAssetFieldRepeatableInstance,
  selectAssetFieldRepeatableInstances,
  selectAssetFieldRepeatableInstancesForGroup,
  selectAssetFieldResults,
  selectAssetListItem,
  selectAssetListItemUnmerged,
  selectAssetOrganisationId,
  selectAssetPlans,
  selectChosenAssetIds,
  selectFilteredAssetsListIds,
  selectIsAssetLoaded,
} from 'store/assets/selectors';
import { Asset, AssetFromSync, FieldTypes } from 'store/assets/types';
import {
  selectUserOrganisationFeatures,
  selectUserOrganisationUuidToUid,
} from 'store/auth/selectors';
import { SET_ASSET_DELTA } from 'store/changes/actions';
import {
  selectChangedAsset,
  selectChangedAssets,
} from 'store/changes/selectors';
import { RBFile } from 'store/files/types';
import {
  selectOrganisationFieldGroup,
  selectOrganisationFields,
} from 'store/organisations/selectors';
import { doLoadPlans } from 'store/plan/actions';
import { Plan } from 'store/plan/types';
import { sagaConfirm } from 'store/sagaHelpers';
import {
  ADD_DOWNLOADED_THUMBNAIL,
  ADD_FILE_TO_UPLOAD_QUEUE,
  ASSETS_DELETED,
} from 'store/sync/actions';
import { call, put, select, take, takeEvery } from 'typed-redux-saga';
import { AppContextEnum, FilesContextEnum } from 'types/types';
import { v4 as uuid } from 'uuid';
import { selectSettingSavePhotosToMediaLibrary } from 'store/app/selectors';
import { smartText, str } from 'lib/str';
import deepMerge from 'deepmerge';
import { CameraView } from 'expo-camera';

function* upgradeAssetData(assetData: Asset) {
  const data = { ...assetData };
  const upgraded = false;
  if (!data.dv) {
    data.dv = {};
  }
  // Do upgrades here...
  if (upgraded) {
    yield* call(setAssetData, data.id, data);
  }
  return data;
}

export function* getMergedAssetFromStorage(
  assetId: string,
): Generator<unknown, Asset> {
  const asset = yield* call(getAssetData, assetId);
  const changes = yield* select(selectChangedAsset, assetId);
  return changes
    ? deepMerge(asset, changes, {
        arrayMerge: (_, a) => a,
      })
    : asset;
}

function* unloadAssetSaga(action: ReturnType<typeof UNLOAD_ASSET>): Generator {
  try {
    if (yield* select(selectIsAssetLoaded, action.payload)) {
      const organisationId = yield* select(
        selectAssetOrganisationId,
        action.payload,
      );
      yield* put(UNSET_LOADED_ASSET(action.payload));
      yield* put(UNLOAD_ORGANISATION(organisationId));
    }
  } catch (error) {
    yield* put(CATCH_ERROR({ error }));
  }
}

function* deleteChosenAssetsSaga(): Generator {
  const assetIds = yield* select(selectChosenAssetIds);
  const changedAssets = yield* select(selectChangedAssets);

  const finalAssetIds = [];
  const badAssetNames = [];

  yield* put(SHOW_BLOCKING_LOADER());

  for (const assetId of assetIds) {
    const badAsset = changedAssets[assetId]
      ? yield* select(selectAssetListItem, assetId)
      : null;
    if (badAsset) {
      badAssetNames.push(badAsset.details.name);
    } else {
      finalAssetIds.push(assetId);
    }
  }

  if (finalAssetIds.length) {
    yield* put(ENQUEUE_ASSET_DELETE(finalAssetIds));
    yield* take(ASSETS_DELETED);
  }

  yield* put(HIDE_BLOCKING_LOADER());

  if (badAssetNames.length) {
    yield* put(
      ALERT({
        text: `${
          assetIds.length === 1
            ? "This asset can't be removed because it has un-synced changes."
            : pluralise(
                badAssetNames,
                "One asset couldn't be removed because it has un-synced changes.",
                "{x} assets couldn't be removed because they have un-synced changes.",
              )
        }\n\nPlease sync these changes and try again`,
      }),
    );
  }
}

export const assetSideEffects = [
  takeEvery(LOAD_ASSET.request, function* f(action) {
    try {
      const assetId = action.payload;
      const assetListItem = yield* select(selectAssetListItemUnmerged, assetId);
      if (!assetListItem) {
        throw new Error('Asset not found locally');
      }

      // Load organisation before the asset
      const lo = LOAD_ORGANISATION;
      yield* put(lo.request(assetListItem.organisation_id));
      const yielded = yield* take([lo.success, lo.failure]);
      if (yielded.type === lo.failure.toString()) {
        throw new Error('Failed to load organisation');
      }

      // If the asset is not already loaded, get it from storage now
      if (!(yield* select(selectIsAssetLoaded, assetId))) {
        const assetData = yield* call(getAssetData, assetId);
        yield* put(SET_LOADED_ASSET(yield* upgradeAssetData(assetData)));
      }
      yield* put(LOAD_ASSET.success(assetId));
    } catch (error) {
      yield* put(CATCH_ERROR({ error }));
      yield* put(LOAD_ASSET.failure(action.payload));
    }
  }),
  takeEvery(UNLOAD_ASSET, unloadAssetSaga),
  takeEvery(DELETE_CHOSEN_ASSETS, deleteChosenAssetsSaga),

  takeEvery(CHOOSE_ALL_FILTERED_ASSETS, function* f() {
    yield* put(CHOOSE_ASSETS(yield* select(selectFilteredAssetsListIds)));
  }),

  takeEvery(doAssetSaveFilesToAsset, function* f(action) {
    const { assetId, eventKey, files, source } = action.payload;
    yield put(SHOW_BLOCKING_LOADER());
    const now = Date.now();
    try {
      const fileErrors: string[] = [];
      const filesDeltaPayload: Asset['files'] = {};
      const addedFileIds: string[] = [];

      for (const file of files) {
        try {
          const fileNameParts = file.name.split('.');
          let fileType = getFileTypeFromUntrustedExtension(
            last(fileNameParts)?.toLowerCase() || '',
          );

          if (!fileType) {
            throw new Error('File type is not allowed');
          }

          if (
            source === FileSourceType.IMAGE &&
            fileType.group !== FileTypeGroupsEnum.image
          ) {
            throw new Error('File must be an image');
          }

          if (source === FileSourceType.FILE && !validateFileSize(file)) {
            throw new Error(
              `File too large; files must be under ${MAX_FILE_SIZE_MB} MB`,
            );
          }

          const fileUuid = uuid();
          const uri = file.uri || (yield* call(readAsDataURLAsync, file.file!));

          if (fileType.group === FileTypeGroupsEnum.image) {
            // Force JPEG; images are resized into JPEGs
            fileType = FILE_TYPES.jpeg!;
            // Resize to large image and save
            yield* call(
              writeLocallyAddedFile,
              fileUuid,
              removeDataBase64Prefix(
                (yield* call(resizeImage, {
                  uri,
                  width: file.width,
                  height: file.height,
                  maxWidth: IMAGE_MAX_WIDTH,
                  maxHeight: IMAGE_MAX_HEIGHT,
                  compression: IMAGE_COMPRESSION,
                })).base64,
              ),
            );
            // Resize to thumbnail and save
            yield* call(
              setAssetThumbnail,
              assetId,
              fileUuid,
              removeDataBase64Prefix(
                (yield* call(resizeImage, {
                  uri,
                  width: file.width,
                  height: file.height,
                  maxWidth: THUMBNAIL_MAX_WIDTH,
                  maxHeight: THUMBNAIL_MAX_HEIGHT,
                  compression: THUMBNAIL_COMPRESSION,
                })).base64,
              ),
            );
            yield* put(
              ADD_DOWNLOADED_THUMBNAIL({
                assetId,
                fileId: fileUuid,
              }),
            );
          } else {
            yield* call(
              writeLocallyAddedFile,
              fileUuid,
              Platform.OS === 'web'
                ? removeDataBase64Prefix(uri)
                : yield* call(new File(uri).base64),
            );
          }
          filesDeltaPayload[fileUuid] = {
            deletable: true,
            editable: true,
            id: fileUuid,
            name: (fileNameParts.length > 1
              ? fileNameParts.slice(0, -1)
              : fileNameParts
            ).join('.'),
            timestamp: Math.floor(now / 1000),
            type: fileType.extension,
          };
          addedFileIds.push(fileUuid);
        } catch (error) {
          if (isError(error)) {
            fileErrors.push(`${file.name}: ${error.message}`);
          }
        }
      }

      if (addedFileIds.length) {
        yield* put(
          SET_ASSET_DELTA({
            delta: {
              files: filesDeltaPayload,
            },
            id: assetId,
          }),
        );

        yield* put(ADD_FILE_TO_UPLOAD_QUEUE(addedFileIds));

        if (eventKey) {
          yield* put(
            eventTypeFilesSelected({
              eventKey,
              fileIds: addedFileIds,
            }),
          );
        }
      }

      if (fileErrors.length) {
        yield put(
          ALERT({
            text: fileErrors.join('\n'),
            title: pluralise(
              fileErrors,
              'Error Adding File',
              'Error Adding {x} Files',
            ),
          }),
        );
      }
    } catch (error) {
      yield put(
        CATCH_ERROR({
          error,
          info: {
            _: 'doAssetSaveFilesToAsset',
            assetId,
            files,
            source,
          },
        }),
      );
    }
    yield put(HIDE_BLOCKING_LOADER());
    yield* put(HIDE_BLOCKING_LOADER('getImagesFromPicker'));
  }),

  takeEvery(doAssetFileGetPhotoFromPhoto, function* f(action) {
    const { assetId, eventKey } = action.payload;

    if (Platform.OS === 'web') {
      if (!(yield* call(CameraView.isAvailableAsync))) {
        yield* put(
          ALERT({
            text: "A camera wasn't found, check your device has a camera and try again",
          }),
        );
      }
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.Camera,
          params: { assetId, eventKey },
        }),
      );
      return;
    }

    // Native
    try {
      const permission = yield* call(hasCameraPermission);
      if (!permission) {
        yield* put(
          ALERT({
            text: 'To take photos, please give RiskBase access to your camera in settings',
            altButton: {
              text: 'Open Settings',
              action: OPEN_NATIVE_APP_SETTINGS(),
            },
          }),
        );
        return;
      }

      let saveToMediaLibrary = false;
      const rawSaveToMediaLibrary = yield* select(
        selectSettingSavePhotosToMediaLibrary,
      );
      if (rawSaveToMediaLibrary === null) {
        const doBackups = yield* sagaConfirm({
          title: 'Save Photos to Media Library',
          text:
            'When taking photos, RiskBase can also save them to your ' +
            `device${str.apos}s media library as a backup\n\n` +
            'This can be changed later in Settings',
          confirmText: 'Backup',
          cancelText: `Don${str.apos}t Backup`,
        });
        yield* put(
          doSetSettingSavePhotosToMediaLibrary({
            enable: !!doBackups,
            ask: false,
          }),
        );
      } else {
        saveToMediaLibrary = rawSaveToMediaLibrary;
      }

      const files = yield* call(getPhoto, {
        saveToMediaLibrary,
      });

      // If the user cancels selection, we won't get a file returned
      if (files.length) {
        yield* put(
          doAssetSaveFilesToAsset({
            assetId,
            files,
            eventKey,
            source: FileSourceType.CAMERA,
          }),
        );
      }
    } catch (error) {
      if (isError(error)) {
        if (error.message === GetPhotoErrors.cannotSaveBackup) {
          yield* put(
            ALERT({
              title: smartText("Can't Take Photo"),
              text: smartText(
                '"Save Photos to Media Library" is enabled in App Settings but RiskBase doesn\'t have access to your media library.\n\n' +
                  'Please check RiskBase has access to your media library or turn off "Save Photos to Media Library" in RiskBase\'s App Settings',
              ),
            }),
          );
        } else {
          yield* put(ALERT_ERROR({ error }));
        }
      }
    }
  }),

  takeEvery(doAssetOpenFiles, function* f(action) {
    try {
      const asset = yield* select(selectAsset, action.payload.assetId);
      yield* put(
        doFilesLoad({
          context: {
            context: FilesContextEnum.localAsset,
            assetId: action.payload.assetId,
          },
          files: Object.values(asset.files).reduce<RBFile[]>((acc, file) => {
            const type = file?.type
              ? getFileTypeFromUntrustedExtension(file.type)
              : undefined;
            if (file && type) {
              acc.push({
                id: file.id,
                name: file.name,
                type,
                timestamp: file.timestamp,
                deletable: file.deletable,
                editable: file.editable,
              });
            }
            return acc;
          }, []),
        }),
      );
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.FILEMANAGER,
          params: {
            multiSelect: !!action.payload.multiSelect,
            promptMessage: action.payload.promptMessage || '',
            eventKey: action.payload.eventKey,
            limitSelectionToFileGroups:
              action.payload.limitSelectionToFileGroups,
          },
        }),
      );
    } catch (error) {
      if (isError(error)) {
        yield* put(ALERT_ERROR({ error }));
      }
      yield* put(CATCH_ERROR({ error }));
    }
  }),

  takeEvery(ASSET_SAVE_FIELDS, function* f(action) {
    const { assetId } = action.payload;
    const organisationId = yield* select(selectAssetOrganisationId, assetId);
    const plans = yield* select(selectAssetPlans, assetId);
    const results = yield* select(selectAssetFieldResults, assetId);
    const fields = yield* select(selectOrganisationFields, organisationId);
    const newResults = action.payload.results;
    const planUpdates: Record<string, Pick<Plan, 'pinCount'>> = {};
    Object.values(newResults).forEach((newResult) => {
      if (fields[newResult.fieldId]?.type === FieldTypes.PIN) {
        const existingResult = results[newResult.fieldId];
        const oldPlanId = existingResult?.pin ? existingResult.pin.planId : '';
        const newPlanId = newResult.pin ? newResult.pin.planId : '';
        if (oldPlanId !== newPlanId) {
          // Remove count from old plan
          const oldPlan = plans[oldPlanId];
          if (oldPlan) {
            planUpdates[oldPlan.id] = {
              pinCount:
                (planUpdates[oldPlan.id]?.pinCount ?? oldPlan.pinCount) - 1,
            };
          }
          // Add count to new plan
          const newPlan = plans[newPlanId];
          if (newPlan) {
            planUpdates[newPlan.id] = {
              pinCount:
                (planUpdates[newPlan.id]?.pinCount ?? newPlan.pinCount) + 1,
            };
          }
        }
      }
    });
    yield* put(
      SET_ASSET_DELTA({
        delta: {
          fieldResults: newResults,
          plans: planUpdates,
        },
        id: assetId,
      }),
    );
  }),

  takeEvery(doAssetFindFieldInstanceByQR, function* f(action) {
    const { assetId, groupId } = action.payload;
    const organisationId = yield* select(selectAssetOrganisationId, assetId);
    const group = yield* select(
      selectOrganisationFieldGroup,
      organisationId,
      groupId,
    );
    if (!group) {
      return;
    }
    yield* put(
      OPEN_BARCODE_SCANNER({
        text: `scan to find an existing ${group.calculatedInfo.singularName.toLowerCase()} or to create a new one`,
        actionAfter: doAssetFoundFieldInstanceByQR({
          assetId,
          groupId,
          data: '',
        }),
      }),
    );
  }),

  takeEvery(doAssetFoundFieldInstanceByQR, function* f(action) {
    const { assetId, groupId, data } = action.payload;
    const organisationId = yield* select(selectAssetOrganisationId, assetId);
    const group = yield* select(
      selectOrganisationFieldGroup,
      organisationId,
      groupId,
    );
    const qrFieldId = group?.calculatedInfo.qrFieldId;
    if (!group || !qrFieldId) {
      return;
    }
    const instances = yield* select(
      selectAssetFieldRepeatableInstancesForGroup,
      assetId,
      groupId,
    );

    const instance = Object.values(instances).find(
      (i) => i.results[qrFieldId]?.value === data,
    );

    if (instance) {
      yield* put(
        doAssetOpenFieldInstance({
          assetId,
          groupId,
          instanceId: instance.instanceId,
        }),
      );
      return;
    }

    if (
      yield* sagaConfirm({
        text:
          `No existing ${group.calculatedInfo.singularName.toLowerCase()} found\n\n` +
          'Create one with that QR code now?',
        confirmText: 'Create',
      })
    ) {
      yield* put(
        NAVIGATE_MODAL({
          screen: ModalScreens.NEWREPEATABLEINSTANCE,
          params: {
            assetId,
            groupId,
            initValues: {
              [qrFieldId]: data,
            },
          },
        }),
      );
    }
  }),

  takeEvery(doAssetFindFieldInstanceByPin, function* f(action) {
    const { assetId, groupId } = action.payload;
    const organisationId = yield* select(selectAssetOrganisationId, assetId);
    const group = yield* select(
      selectOrganisationFieldGroup,
      organisationId,
      groupId,
    );
    if (!group) {
      return;
    }
    const pins = yield* select(
      selectAssetFieldRepeatableGroupPins,
      assetId,
      groupId,
    );
    if (!pins.length) {
      yield* put(
        ALERT({
          text: `No ${group.name.toLowerCase()} have pins yet. You'll be able to find them by their pins once there are`,
        }),
      );
      return;
    }
    yield* put(
      doAssetLoadPlans({
        findNoun: group.calculatedInfo.singularName,
        assetId,
        pins,
        actionAfterFind: doAssetFoundFieldInstanceByPin({
          assetId,
          groupId,
          id: '',
        }),
      }),
    );
  }),

  takeEvery(doAssetFoundFieldInstanceByPin, function* f(action) {
    const { assetId, groupId, id } = action.payload;
    yield* put(doAssetOpenFieldInstance({ assetId, groupId, instanceId: id }));
  }),

  takeEvery(doAssetOpenFieldInstance, function* f(action) {
    const { assetId, instanceId, groupId } = action.payload;
    navigate(
      Stacks.ASSETFIELDREPEATABLE,
      Screens.ASSETFIELDREPEATABLE_INSTANCE,
      {
        assetId,
        groupId,
        instanceId,
      },
    );
  }),

  takeEvery(doAssetDeleteFieldInstance, function* f(action) {
    const { assetId, instanceId } = action.payload;
    const instance = yield* select(
      selectAssetFieldRepeatableInstance,
      assetId,
      instanceId,
    );
    if (!instance) {
      return;
    }

    const organisationId = yield* select(selectAssetOrganisationId, assetId);
    const group = yield* select(
      selectOrganisationFieldGroup,
      organisationId,
      instance.groupId,
    );

    if (!group) {
      return;
    }

    if (
      !(yield* sagaConfirm({
        title: `Delete ${group.calculatedInfo.singularName}`,
        text: 'Deleting this cannot be undone',
        confirmText: 'Delete',
        destructive: true,
      }))
    ) {
      return;
    }

    yield* put(
      ASSET_SAVE_REPEATABLE_FIELDS({
        assetId,
        instance: {
          ...instance,
          autoId: instance.autoId || 1,
          updatedTs: dateToNumber(),
          results: {},
          action: 'delete',
        },
      }),
    );

    yield* put(doToast(`${group.calculatedInfo.singularName} Deleted`));
  }),

  takeEvery(ASSET_SAVE_REPEATABLE_FIELDS, function* f(action) {
    const { assetId } = action.payload;
    const organisationId = yield* select(selectAssetOrganisationId, assetId);
    const plans = yield* select(selectAssetPlans, assetId);
    const newInstance = action.payload.instance;
    const fields = yield* select(selectOrganisationFields, organisationId);
    const instance = (yield* select(
      selectAssetFieldRepeatableInstances,
      assetId,
    ))[newInstance.instanceId];
    //  as
    //   | FieldRepeatableResultWithNullResults
    //   | undefined;

    const group = yield* select(
      selectOrganisationFieldGroup,
      organisationId,
      newInstance.groupId,
    );

    if (!group) {
      return;
    }

    const planUpdates: Record<string, Pick<Plan, 'pinCount'>> = {};

    if (newInstance.action === 'delete') {
      Object.values(instance?.results || {}).forEach((existingResult) => {
        // Remove all counts from pin results
        if (fields[existingResult.fieldId]?.type === FieldTypes.PIN) {
          const oldPlanId = existingResult.pin ? existingResult.pin.planId : '';
          const oldPlan = plans[oldPlanId];
          if (oldPlan) {
            planUpdates[oldPlanId] = {
              pinCount:
                (planUpdates[oldPlan.id]?.pinCount ?? oldPlan.pinCount) - 1,
            };
          }
        }
      });
    } else {
      Object.values(newInstance.results).forEach((newResult) => {
        if (fields[newResult.fieldId]?.type === FieldTypes.PIN) {
          const existingResult = instance?.results[newResult.fieldId];
          const oldPlanId = existingResult?.pin
            ? existingResult.pin.planId
            : '';
          const newPlanId = newResult.pin ? newResult.pin.planId : '';
          if (oldPlanId !== newPlanId) {
            // Remove count from old plan
            const oldPlan = plans[oldPlanId];
            if (oldPlan) {
              planUpdates[oldPlan.id] = {
                pinCount:
                  (planUpdates[oldPlan.id]?.pinCount ?? oldPlan.pinCount) - 1,
              };
            }
            // Add count to new plan
            const newPlan = plans[newPlanId];
            if (newPlan) {
              planUpdates[newPlan.id] = {
                pinCount:
                  (planUpdates[newPlan.id]?.pinCount ?? newPlan.pinCount) + 1,
              };
            }
          }
        }
      });
    }

    yield* put(
      SET_ASSET_DELTA({
        id: assetId,
        delta: {
          fieldInstances: {
            [newInstance.instanceId]: {
              instanceId: newInstance.instanceId,
              groupId: newInstance.groupId,
              autoId: instance?.autoId || newInstance.autoId,
              reference: instance?.reference || newInstance.reference,
              results: newInstance.results,
              createdTs: instance?.createdTs || newInstance.updatedTs,
              updatedTs: newInstance.updatedTs,
              action: newInstance.action,
            },
          },
          plans: planUpdates,
        },
      }),
    );

    if (!instance) {
      yield* put(doToast(`${group.calculatedInfo.singularName} Added`));
    }
  }),

  takeEvery(doAssetOpenOverview, function* f(action) {
    const { assetId } = action.payload;
    const screenSize = getScreenSize();
    if (screenSize.size === ScreenSize.large) {
      replace(Stacks.ASSET, Screens.ASSET_SUMMARY, { assetId });
    } else {
      navigate(Stacks.ASSET, Screens.ASSET_SUMMARY, { assetId });
    }
  }),

  takeEvery(SET_ASSET_DELTA, function* f(action) {
    if (yield* select(selectIsAssetLoaded, action.payload.id)) {
      yield* put(
        eventAssetDataUpdated({
          route: 'local',
          assetId: action.payload.id,
          data: yield* select(selectAsset, action.payload.id),
          updatedKeys: Object.keys(action.payload.delta) as Partial<
            (keyof AssetFromSync)[]
          >,
        }),
      );
    }
  }),

  takeEvery(doAssetLoadPlans, function* f(action) {
    const userFeatures = yield* select(
      selectUserOrganisationFeatures,
      yield* select(
        selectUserOrganisationUuidToUid,
        yield* select(selectAssetOrganisationId, action.payload.assetId),
      ),
    );

    yield* put(
      doLoadPlans({
        ...action.payload,
        context: {
          context: AppContextEnum.localAsset,
          assetId: action.payload.assetId,
        },
        plansEditable: userFeatures.editassetplans,
        plans: Object.entries(
          yield* select(selectAssetPlans, action.payload.assetId),
        ).reduce<Record<string, Plan>>((acc, [planId, plan]) => {
          acc[planId] = {
            id: plan.id,
            name: plan.name,
            fileId: plan.fileId,
            filePage: plan.filePage,
            fileType: plan.fileType || 'image',
            fileWidth: plan.fileWidth || 0,
            fileHeight: plan.fileHeight || 0,
            pinCount: plan.pinCount,
          };
          return acc;
        }, {}),
      }),
    );
  }),
];
